import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Grid,
  Input,
  List,
  Modal,
  Popconfirm,
  Skeleton,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import ReusableUpload from "../../components/upload/ReusableUpload";
import {
  getSelectedOrganizationId,
  getSelectedOrganizationName,
  hasWorkingOrganization,
} from "../../api/Helper";
import {
  DemandForecastArtifactFile,
  DemandForecastArtifactKind,
  deleteDemandForecastArtifact,
  downloadDemandForecastArtifact,
  getDemandForecastArtifactSelection,
  listDemandForecastArtifacts,
  renameDemandForecastArtifact,
  setActiveDemandForecastArtifact,
  uploadDemandForecastArtifact,
} from "../../api/Services";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";
import { getTablePagination } from "../../helpers/TablePaginationHelper";
import styles from "./DemandForecastArtifacts.module.scss";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const KINDS: {
  kind: DemandForecastArtifactKind;
  titleKey: string;
  hintKey: string;
  accept: string;
}[] = [
  {
    kind: "pipeline",
    titleKey: "DemandForecastArtifacts-Pipeline",
    hintKey: "DemandForecastArtifacts-PipelineHint",
    accept: ".joblib",
  },
  {
    kind: "model",
    titleKey: "DemandForecastArtifacts-Model",
    hintKey: "DemandForecastArtifacts-ModelHint",
    accept: ".joblib",
  },
  {
    kind: "config",
    titleKey: "DemandForecastArtifacts-Config",
    hintKey: "DemandForecastArtifacts-ConfigHint",
    accept: ".json,application/json",
  },
];

type DemandForecastArtifactsProps = {
  embedded?: boolean;
};

const formatBytes = (size?: number) => {
  if (size == null || Number.isNaN(size)) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const ArtifactSection = ({
  kind,
  hintKey,
  accept,
  organizationId,
  onChanged,
}: {
  kind: DemandForecastArtifactKind;
  hintKey: string;
  accept: string;
  organizationId: number;
  onChanged: () => void;
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobileList = !screens.md;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<DemandForecastArtifactFile[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [page, setPage] = useState(1);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<DemandForecastArtifactFile | null>(
    null
  );
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listDemandForecastArtifacts(
        organizationId,
        kind,
        page - 1,
        10
      );
      setFiles(result.items);
      setTotalSize(result.totalElements);
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    } finally {
      setLoading(false);
    }
  }, [organizationId, kind, page, t]);

  useEffect(() => {
    setPage(1);
    setFileList([]);
  }, [organizationId, kind]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUploadRequest = async (options: {
    file: File;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  }) => {
    const raw = options.file as File & { originFileObj?: File };
    const fileToUpload = raw.originFileObj ?? raw;
    setUploading(true);
    try {
      await uploadDemandForecastArtifact(organizationId, kind, fileToUpload);
      message.success(t("DemandForecastArtifacts-UploadSuccess"));
      options.onSuccess();
      setFileList([]);
      setPage(1);
      await fetchFiles();
      onChanged();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("DemandForecastArtifacts-UploadError")));
      options.onError(e);
    } finally {
      setUploading(false);
    }
  };

  const handleSetActive = async (fileId: string | null) => {
    try {
      await setActiveDemandForecastArtifact(organizationId, kind, fileId);
      message.success(
        fileId
          ? t("DemandForecastArtifacts-SetActiveSuccess")
          : t("DemandForecastArtifacts-DeactivateSuccess")
      );
      await fetchFiles();
      onChanged();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await deleteDemandForecastArtifact(fileId);
      message.success(t("DemandForecastArtifacts-DeleteSuccess"));
      await fetchFiles();
      onChanged();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    }
  };

  const handleDownload = async (record: DemandForecastArtifactFile) => {
    setDownloadingId(record.fileId);
    try {
      await downloadDemandForecastArtifact(record.fileId, record.originalFilename);
    } catch (e) {
      message.error(
        getApiErrorMessage(e, t("DemandForecastArtifacts-DownloadError"))
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRename = async () => {
    if (!renameTarget) return;
    setRenaming(true);
    try {
      await renameDemandForecastArtifact(renameTarget.fileId, renameValue.trim());
      message.success(t("DemandForecastArtifacts-RenameSuccess"));
      setRenameTarget(null);
      await fetchFiles();
      onChanged();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
    } finally {
      setRenaming(false);
    }
  };

  const renderActions = (record: DemandForecastArtifactFile, compact: boolean) => (
    <div className={styles.actions}>
      <Tooltip title={t("Common-Download")}>
        <Button
          type={compact ? "default" : "link"}
          size={compact ? "small" : "middle"}
          icon={<DownloadOutlined />}
          loading={downloadingId === record.fileId}
          onClick={() => handleDownload(record)}
        />
      </Tooltip>
      <Tooltip title={t("DemandForecastArtifacts-Rename")}>
        <Button
          type={compact ? "default" : "link"}
          size={compact ? "small" : "middle"}
          icon={<EditOutlined />}
          onClick={() => {
            setRenameTarget(record);
            setRenameValue(record.originalFilename || "");
          }}
        />
      </Tooltip>
      {record.active ? (
        <Tooltip title={t("DemandForecastArtifacts-Deactivate")}>
          <Button
            type={compact ? "default" : "link"}
            size={compact ? "small" : "middle"}
            icon={<CloseOutlined />}
            onClick={() => handleSetActive(null)}
          />
        </Tooltip>
      ) : (
        <Tooltip title={t("DemandForecastArtifacts-SetActive")}>
          <Button
            type={compact ? "default" : "link"}
            size={compact ? "small" : "middle"}
            icon={<CheckOutlined />}
            onClick={() => handleSetActive(record.fileId)}
          />
        </Tooltip>
      )}
      <Popconfirm
        title={t("DemandForecastArtifacts-DeleteConfirm")}
        onConfirm={() => handleDelete(record.fileId)}
        okText={t("Common-Yes")}
        cancelText={t("Common-No")}
      >
        <Tooltip title={t("Common-Delete")}>
          <Button
            type={compact ? "default" : "link"}
            danger
            size={compact ? "small" : "middle"}
            icon={<DeleteOutlined />}
          />
        </Tooltip>
      </Popconfirm>
    </div>
  );

  const columns = [
    {
      title: t("DemandForecastArtifacts-Filename"),
      dataIndex: "originalFilename",
      key: "originalFilename",
      ellipsis: true,
    },
    {
      title: t("DemandForecastArtifacts-Size"),
      dataIndex: "size",
      key: "size",
      width: 110,
      render: (size: number) => formatBytes(size),
      responsive: ["lg" as const],
    },
    {
      title: t("DemandForecastArtifacts-UploadedAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      ellipsis: true,
      render: (value?: string) => (value ? new Date(value).toLocaleString() : "-"),
      responsive: ["xl" as const],
    },
    {
      title: t("DemandForecastArtifacts-Status"),
      dataIndex: "active",
      key: "active",
      width: 120,
      render: (active: boolean) =>
        active ? (
          <Tag color="green">{t("DemandForecastArtifacts-Active")}</Tag>
        ) : (
          <Tag>{t("DemandForecastArtifacts-Inactive")}</Tag>
        ),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 180,
      render: (_: unknown, record: DemandForecastArtifactFile) =>
        renderActions(record, true),
    },
  ];

  return (
    <Card className={styles.section}>
      <Text type="secondary">{t(hintKey)}</Text>
      <div className={styles.uploadRow}>
        <Text strong>{t("DemandForecastArtifacts-Upload")}</Text>
        <div style={{ marginTop: 8 }}>
          <ReusableUpload
            accept={accept}
            loading={uploading}
            disabled={uploading}
            fileList={fileList}
            onChange={(info) => setFileList(info.fileList)}
            onUploadRequest={handleUploadRequest}
            mode="full"
            fullWidth
            showUploadButton
          />
        </div>
      </div>
      {loading && files.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : files.length === 0 ? (
        <Text type="secondary" style={{ display: "block", marginTop: 16 }}>
          {t("DemandForecastArtifacts-Empty")}
        </Text>
      ) : isMobileList ? (
        <List
          style={{ marginTop: 16 }}
          dataSource={files}
          pagination={
            {
              current: page,
              total: totalSize,
              pageSize: 10,
              onChange: (nextPage: number) => setPage(nextPage),
              showSizeChanger: false,
            } as any
          }
          renderItem={(record) => (
            <Card className={styles.mobileCard} size="small">
              <Text strong ellipsis>
                {record.originalFilename}
              </Text>
              <div className={styles.mobileCardMeta}>
                <Text type="secondary">
                  {t("DemandForecastArtifacts-Size")}: {formatBytes(record.size)}
                </Text>
                <Text type="secondary">
                  {t("DemandForecastArtifacts-UploadedAt")}:{" "}
                  {record.createdAt
                    ? new Date(record.createdAt).toLocaleString()
                    : "-"}
                </Text>
                {record.active ? (
                  <Tag color="green">{t("DemandForecastArtifacts-Active")}</Tag>
                ) : (
                  <Tag>{t("DemandForecastArtifacts-Inactive")}</Tag>
                )}
              </div>
              <div className={styles.mobileCardActions}>
                {renderActions(record, false)}
              </div>
            </Card>
          )}
        />
      ) : (
        <div className={styles.tableWrap}>
          <Table
            rowKey="fileId"
            loading={loading && files.length > 0}
            columns={columns}
            dataSource={files}
            pagination={getTablePagination(t, {
              current: page,
              total: totalSize,
              onChange: (nextPage) => setPage(nextPage),
            })}
            tableLayout="fixed"
          />
        </div>
      )}
      <Modal
        title={t("DemandForecastArtifacts-Rename")}
        visible={renameTarget != null}
        onCancel={() => setRenameTarget(null)}
        onOk={handleRename}
        confirmLoading={renaming}
        okText={t("Common-Save")}
        cancelText={t("Common-Cancel")}
        destroyOnClose
      >
        <Input
          value={renameValue}
          onChange={(event) => setRenameValue(event.target.value)}
          placeholder={t("DemandForecastArtifacts-RenameFilename")}
        />
      </Modal>
    </Card>
  );
};

const DemandForecastArtifacts = ({
  embedded = false,
}: DemandForecastArtifactsProps) => {
  const { t } = useTranslation();
  const [activeKind, setActiveKind] =
    useState<DemandForecastArtifactKind>("pipeline");
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selection, setSelection] = useState<
    Partial<Record<DemandForecastArtifactKind, DemandForecastArtifactFile | null>>
  >({});
  const selectionRequest = useRef(0);
  const workingOrganizationId =
    hasWorkingOrganization() && getSelectedOrganizationId()
      ? Number(getSelectedOrganizationId())
      : undefined;
  const workingOrganizationName = hasWorkingOrganization()
    ? getSelectedOrganizationName()
    : undefined;
  const activeSection = KINDS.find((item) => item.kind === activeKind) ?? KINDS[0];

  const loadSelection = useCallback(async () => {
    if (!workingOrganizationId) {
      setSelection({});
      return;
    }
    const requestId = ++selectionRequest.current;
    setSelectionLoading(true);
    try {
      const next = await getDemandForecastArtifactSelection(workingOrganizationId);
      if (requestId === selectionRequest.current) {
        setSelection(next);
      }
    } catch (e) {
      if (requestId === selectionRequest.current) {
        message.error(getApiErrorMessage(e, t("Common-ErrorMessage")));
      }
    } finally {
      if (requestId === selectionRequest.current) {
        setSelectionLoading(false);
      }
    }
  }, [workingOrganizationId, t]);

  useEffect(() => {
    setActiveKind("pipeline");
    setSelection({});
  }, [workingOrganizationId]);

  useEffect(() => {
    loadSelection();
  }, [loadSelection]);

  const content = (
    <div className={styles.wrapper} style={embedded ? { padding: 0 } : undefined}>
      {!embedded && (
        <div className={styles.header}>
          <Title level={3}>{t("DemandForecastArtifacts-Title")}</Title>
          <Text type="secondary">{t("DemandForecastArtifacts-Description")}</Text>
        </div>
      )}
      <Text type="secondary" className={styles.note}>
        {t("DemandForecastArtifacts-PairNote")}
      </Text>
      {!workingOrganizationId ? (
        <Card className={styles.section}>
          <Text type="secondary">
            {t("DemandForecastArtifacts-SelectOrganizationFirst")}
          </Text>
        </Card>
      ) : (
        <>
          <Text style={{ display: "block", marginTop: 12 }}>
            <Text strong>{t("DemandForecastArtifacts-Organization")}: </Text>
            {workingOrganizationName}
          </Text>
          <Card className={styles.summary}>
            <Text strong>{t("DemandForecastArtifacts-SelectionTitle")}</Text>
            {selectionLoading && Object.keys(selection).length === 0 ? (
              <div className={styles.summarySkeleton}>
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              </div>
            ) : (
              <div className={styles.summaryGrid}>
                {KINDS.map((item) => {
                  const selected = selection[item.kind];
                  const isCurrent = item.kind === activeKind;
                  return (
                    <button
                      key={item.kind}
                      type="button"
                      className={`${styles.summaryItem} ${
                        isCurrent ? styles.summaryItemCurrent : ""
                      }`}
                      onClick={() => setActiveKind(item.kind)}
                    >
                      <Text type="secondary" className={styles.summaryLabel}>
                        {t(item.titleKey)}
                      </Text>
                      <Text
                        strong
                        ellipsis
                        className={styles.summaryValue}
                        type={selected ? undefined : "secondary"}
                      >
                        {selected?.originalFilename ||
                          t("DemandForecastArtifacts-SelectionNone")}
                      </Text>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
          <Tabs
            activeKey={activeKind}
            onChange={(key) => setActiveKind(key as DemandForecastArtifactKind)}
            className={styles.kindTabs}
          >
            {KINDS.map((item) => (
              <TabPane tab={t(item.titleKey)} key={item.kind} />
            ))}
          </Tabs>
          <ArtifactSection
            key={activeSection.kind}
            kind={activeSection.kind}
            hintKey={activeSection.hintKey}
            accept={activeSection.accept}
            organizationId={workingOrganizationId}
            onChanged={loadSelection}
          />
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }
  return <AdminValidation>{content}</AdminValidation>;
};

export default DemandForecastArtifacts;
